import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import * as styles from './ActivityItem.module.scss';
import AvatarStack from '../../components/AvatarStack';
import Button from '../../components/Button';
import SVGIcon from '../../components/SVGIcon';
import Timestamp from '../../components/Timestamp';
import Tooltip from '../../components/Tooltip';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import arrowUpRightIcon from '../../icons/non-prefilled/16x16/arrowUpRight.svg';
import getFirstName from '../../utils/text/getFirstName';

const ACTIVITY_ITEM_ACTORS_FRAGMENT = gql(/** @complexity 25 */ `
  fragment ActivityItem_Actors on User {
    id
    ...AvatarStack_users
  }
`);

type ActivityItemProps = {
  icon: string | React.ReactNode;
  description?: React.ReactNode;
  timestamp?: string | number;
  actors?: DocumentType<typeof ACTIVITY_ITEM_ACTORS_FRAGMENT>[];
  withActorsNames?: boolean;
  onClick?: () => void;
  href?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

const ActivityItem = ({
  actors,
  withActorsNames,
  icon,
  description,
  timestamp,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ActivityItemProps) => {
  const hasTwoRows = !!timestamp || !!actors?.length;

  const isInteractive = !!onClick || !!href;
  const Component = isInteractive ? Button : 'div';
  return (
    <Component
      className={classNames(styles.activityItem, {
        [styles.isInteractive]: isInteractive,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      to={href}
    >
      <div className={styles.activityInner}>
        <div className={styles.hoverIndicator} />
        <div className={styles.activityVerticalLine} />
        <div className={styles.activityIcon}>
          {typeof icon === 'string' ? (
            <SVGIcon size={16}>{icon}</SVGIcon>
          ) : (
            icon
          )}
        </div>
        <div className={styles.activityFirstRow}>
          {!hasTwoRows && description}
          {hasTwoRows && !!actors?.length && (
            <>
              <span className={styles.activityAvatars}>
                <AvatarStack
                  items={actors}
                  noWrap
                  size={20}
                  limit={5}
                  countOverflowVariant="circle"
                  spacing="tight"
                  borderSize={2}
                />
              </span>
              {withActorsNames && (
                <span className={styles.activityNames}>
                  {actors.map((actor) => getFirstName(actor.name)).join(', ')}
                </span>
              )}
            </>
          )}
          {hasTwoRows && !!timestamp && (
            <Tooltip
              asChild
              content={formatActivityTimeTooltip(moment(timestamp))}
            >
              <span className={styles.activityTime}>
                <Timestamp date={timestamp} format={formatActivityTime} />
              </span>
            </Tooltip>
          )}
          {!!href && (
            <SVGIcon className={styles.activityLinkArrow}>
              {arrowUpRightIcon}
            </SVGIcon>
          )}
        </div>
        {hasTwoRows && (
          <div className={styles.activitySecondRow}>{description}</div>
        )}
      </div>
    </Component>
  );
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const formatActivityTime = (momentDate: moment.Moment) => {
  const diff = moment.duration(Date.now() - momentDate.valueOf());
  if (Math.round(diff.asSeconds()) < 60) return 'just now';
  if (Math.round(diff.asMinutes()) < 45)
    return `${Math.round(diff.asMinutes())}min ago`;
  if (Math.round(diff.asHours()) < 22)
    return `${Math.round(diff.asHours())}h ago`;
  if (Math.round(diff.asHours()) < 36) return 'a day ago';
  const isSameYear = momentDate.isSame(moment(), 'year');
  return momentDate.format(isSameYear ? 'MMM D' : 'MMM D, YYYY');
};

const formatActivityTimeTooltip = (momentDate: moment.Moment) => {
  return momentDate.format(
    moment().isSame(momentDate, 'year') ? 'MMM D, h:mma' : 'MMM D, YYYY, h:mma',
  );
};

interface ActivitySeparatorProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export const ActivitySeparator = ({
  children,
  onClick,
}: ActivitySeparatorProps) => {
  return (
    <ActivityItem
      icon={<div className={styles.activitySeparatorIcon} />}
      description={
        <div className={styles.activitySeparatorText}>{children}</div>
      }
      onClick={onClick}
    />
  );
};

export default ActivityItem;
